//@ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, useStore as useReduxStore } from 'react-redux';
import { useStore } from '../../store/store';
import { useNav } from 'src/onboarding/hooks/nav';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import {
  selectPaymentSystem,
  ProductDetails,
  billingSelectors,
} from 'core/store/billing/selectors';
import { PaymentSystem } from '@web-solutions/react-billing';
import { processEmail } from 'core/store/profile/actions';
import { reInit as reInitRemoteConfig } from '@web-solutions/core/store/remote-config/actions';
import { init, initOrders } from 'core/store/billing/actions';
import {
  setupStripePayment,
  pinStripeOnetimeProductByName,
} from 'src/onboarding/helpers/setupStripePayment';
import instance from 'src/onboarding/api/interceptor';
import { useAppSelector } from '@web-solutions/store/types';
import { useUpsaleMethods } from 'core/upsale/hooks';
import { usePayment } from 'core/payment/payment-processor-provider/paymentProcessorContext';
import { OneTimePayment } from 'core/one-time';
import FailedPaymentPopup from '../upsale-dynamic/failedPaymentPopup';
import SuccessPayment from '../webUpsale/components/successPaymentPopup';
import { sendEmail } from 'core/upsale/api/send-email';
import { useLocale } from 'core/hooks/use-locale';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { APP_LINKS } from 'src/constants/general';
import Attribution from '@web-solutions/module-attribution';
import Analytics from '@web-solutions/module-analytics';
import { getMagnusToken } from 'src/store/app/utils';
import packageInfo from '../../../../package.json';
import { load } from 'src/store/app/actions';
import { OneClickPayment } from 'core/one-time/one-click';
import { PaymentProcessor } from 'core/payment/payment-processor-provider/components';

const STORAGE_KEY_IDFM = '@RNMV/IDFM';

const params = new URLSearchParams(window.location.search);
const idParam = params.get('id');
const productParam = params.get('product');

const mappingProduct = (product: any, paymentType: PaymentSystem): ProductDetails =>
  ({
    id: paymentType === PaymentSystem.TRUEGATE ? product?.productCode : product?.solidgateId,
    productCode: product?.productCode,
    priceText: product?.amount,
    amount: product?.amount,
    currency: product?.currency,
    product: paymentType === PaymentSystem.TRUEGATE ? product?.productCode : product?.solidgateId,
    isOneTimePurchase: true,
    buttonTitle: product?.buttonTitle,
    title: product?.productTitle,
    totalAmount: product?.totalAmount,
    oldAmount: product?.oldAmount,
    ...(product?.trigger && { trigger: product.trigger }),
  }) as unknown as ProductDetails;

const PaymentForm = ({ rawProduct, rawProducts, paymentType, onSuccess }) => {
  const { onNext } = useNav();
  const { otpRef } = usePayment();
  const dispatch = useDispatch();
  const { locale } = useLocale();
  const purchase = useAppSelector((state: any) => state.billing.purchase);

  const activeProduct = useMemo(
    () => mappingProduct(rawProduct, paymentType),
    [rawProduct?.productCode, paymentType]
  );

  const oneTimeProducts = useMemo(
    () => rawProducts.map((p) => mappingProduct(p, paymentType)),
    [rawProducts, paymentType]
  );

  const { handleActiveProduct, handleOpen } = useUpsaleMethods({ product: rawProduct });
  // ← handleClick убран полностью

  const [showFailedPopup, setShowFailedPopup] = useState(false);


  const billingPending = useAppSelector(
    (state: any) => state.billing.pending || state.billing.orderPending
  );
  const wasLoadingRef = useRef(false);
  const didOpenRef = useRef(false);

  useEffect(() => {
    handleActiveProduct();
    handleOpen();
  }, []);

  useEffect(() => {
    if (billingPending) wasLoadingRef.current = true;
  }, [billingPending]);

  useEffect(() => {
    if (wasLoadingRef.current && !billingPending && !didOpenRef.current) {
      didOpenRef.current = true;
      otpRef.current?.showPaymentPopup();
    }
  }, [billingPending]);

  // Stripe has no order-creation step, so the billingPending-based auto-open
  // above never fires. Open the card form once for Stripe.
  useEffect(() => {
    if (paymentType === PaymentSystem.STRIPE && !didOpenRef.current) {
      didOpenRef.current = true;
      otpRef.current?.showPaymentPopup();
    }
  }, [paymentType]);

  const handleSuccess = () => {
    Analytics.trackEvent('upsale', 'purchase_success', {
      product: rawProduct?.productCode,
      amount: rawProduct?.amount,
    });

    const sendMessage = async () => {
      if (rawProduct?.messageTemplate && purchase?.email) {
        await sendEmail(purchase.email, `${rawProduct.messageTemplate}_${locale}`);
        Analytics.trackEvent('email', 'upsale_purchase', {
          product_name: rawProduct.productCode,
          billing_date: new Date().toISOString().split('T')[0],
        });
      }
    };

    sendMessage().finally(() => {
      onSuccess(null);
    });
  };

  const handleError = (error) => {
    // `message` is already the backend's text ("Your card was declined.") —
    // core/one-time's payment handlers dig it out of the response body.
    Analytics.trackEvent('change_payment_method', 'error', {
      product: rawProduct?.productCode,
      code: error?.code,
      message: error?.message,
      paymentSystem: error?.paymentSystem || paymentType,
    });

    setShowFailedPopup(true);
  };

  return (
    <>
      <OneTimePayment
        ref={otpRef}
        category='upsale'
        trigger={activeProduct?.trigger || 'sub_upsale'}
        activeProduct={activeProduct}
        oneTimeProducts={oneTimeProducts}
        onSuccess={handleSuccess}
        onError={handleError}
        onClose={() => onNext()}
      />

      {showFailedPopup && (
        <FailedPaymentPopup
          onClose={() => setShowFailedPopup(false)}
          onRetry={() => {
            setShowFailedPopup(false);
            otpRef.current?.showPaymentPopup();
          }}
        />
      )}
    </>
  );
};

const ChangePaymentMethod = () => {
  const { changePaymentMethod } = useRemoteConfig();
  const paymentType = useSelector(selectPaymentSystem);
  const setEmail = useStore((state) => state.setEmail);
  const dispatch = useDispatch();
  const reduxStore = useReduxStore();
  const { onNext } = useNav();

  const isInitBilling = useAppSelector(billingSelectors.isInitBilling);
  const [emailReady, setEmailReady] = useState(!idParam);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  const rawProducts = changePaymentMethod?.products ?? [];
  const rawProduct = productParam
    ? rawProducts.find((p) => p.productCode === productParam)
    : rawProducts[0];

  useEffect(() => {
    if (!idParam) return;

    const alreadyReloaded = sessionStorage.getItem('idfm_reloaded');

    instance
      .get(`/profile/${idParam}/email`)
      .then(async ({ data }) => {
        if (data?.idfm && !alreadyReloaded) {
          await AsyncStorage.setItem(STORAGE_KEY_IDFM, data.idfm);
          sessionStorage.setItem('idfm_reloaded', '1');
          window.location.reload();
          return;
        }
        if (data?.email) {
          setEmail(data.email);
          dispatch(processEmail(data.email));
          dispatch(reInitRemoteConfig());

          const paymentSystem = selectPaymentSystem(reduxStore.getState());

          if (paymentSystem === PaymentSystem.STRIPE) {
            // Stripe: fetch config (key) + onetime products, authorize with the
            // existing user's token (no customer creation), then make sure the
            // product named in the URL is the one charged via one-time-payment.
            await setupStripePayment({
              email: data.email,
              dispatch,
              paymentSystem,
              authPayload: data,
              onetimeOnly: true,
            });
            pinStripeOnetimeProductByName(productParam);
          } else {
            dispatch(init());
          }
        }
      })
      .catch(console.error)
      .finally(() => setEmailReady(true));
  }, []);

  if (!emailReady || !isInitBilling) {
    return (
      <div className='page-loader'>
        <svg xmlns='http://www.w3.org/2000/svg' width='74px' height='74px' viewBox='0 0 100 100'>
          <circle
            cx='50'
            cy='50'
            fill='none'
            stroke='#666'
            strokeWidth='10'
            r='35'
            strokeDasharray='164.93361431346415 56.97787143782138'
          />
        </svg>
      </div>
    );
  }

  return (
    <>
      <PaymentForm
        rawProduct={rawProduct}
        rawProducts={rawProducts}
        paymentType={paymentType}
        onSuccess={() => {
          setShowSuccessPopup(true);
          setTimeout(() => setShowCheck(true), 400);
          setTimeout(() => onNext(), 1800);
        }}
      />

      {showSuccessPopup && (
        <SuccessPayment
          showCheck={showCheck}
          onClose={() => {
            setShowSuccessPopup(false);
            onNext();
          }}
        />
      )}
    </>
  );
};

export default ChangePaymentMethod;
